import { useState } from 'react'
import { useBrandColors } from '../../hooks/useBrandColors'

export default function TimeOff() {
  const { accent, accentLight, accentBg } = useBrandColors()
  const [requestType, setRequestType] = useState('Vacation')
  const [startDate, setStartDate] = useState('2025-04-23')
  const [endDate, setEndDate] = useState('2025-04-25')
  const [hoursPerDay, setHoursPerDay] = useState(8)
  const [notes, setNotes] = useState('')
  const [submitted, setSubmitted] = useState(false)

  // Maine LD 369: 1hr accrued per 40hrs worked
  // Demo: employee has worked ~480hrs YTD = 12hrs accrued, 8hrs used = 4hrs available
  const accrued = 12
  const usedYTD = 8
  const available = accrued - usedYTD

  function calcBusinessDays(start, end) {
    let count = 0
    let cur = new Date(start)
    const e = new Date(end)
    while (cur <= e) {
      const d = cur.getDay()
      if (d !== 0 && d !== 6) count++
      cur.setDate(cur.getDate() + 1)
    }
    return count
  }

  const businessDays = calcBusinessDays(startDate, endDate)
  const hoursRequested = businessDays * hoursPerDay
  const hoursAfter = available - hoursRequested
  const balanceWidth = Math.max(0, (available / accrued) * 100)
  const requestedWidth = Math.min(100 - balanceWidth, (hoursRequested / accrued) * 100)

  const history = [
    { dates: 'Mar 7',     type: 'Sick',     hours: 8,  status: 'Approved', statusColor: '#4A7C59' },
    { dates: 'Feb 17–18', type: 'Vacation',  hours: 16, status: 'Approved', statusColor: '#4A7C59' },
    { dates: 'Jan 3',     type: 'Personal', hours: 4,  status: 'Approved', statusColor: '#4A7C59' },
  ]

  const categories = ['Vacation', 'Sick', 'Personal', 'Unpaid']

  function handleSubmit() {
    setSubmitted(true)
    setTimeout(() => setSubmitted(false), 4000)
  }

  return (
    <div style={{fontFamily:"'Montserrat',sans-serif"}}>
      <div style={{marginBottom:20}}>
        <h1 style={{fontFamily:'Georgia,serif',fontSize:'1.4rem',color:'#2B2B2B',fontWeight:600}}>Time Off</h1>
        <p style={{fontSize:11,color:'#6B7D6B',marginTop:2}}>Request time off — tracked in hours per Maine LD 369</p>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>

        {/* Request form */}
        <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
          <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA'}}>
            <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>New Request</div>
          </div>
          <div style={{padding:'16px'}}>

            {submitted && (
              <div style={{display:'flex',alignItems:'center',gap:8,background:'rgba(74,124,89,0.1)',border:'1px solid #4A7C59',borderRadius:8,padding:'10px 14px',marginBottom:16}}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="#4A7C59" strokeWidth="1.5"/><path d="M5 8l2 2 4-4" stroke="#4A7C59" strokeWidth="1.5" strokeLinecap="round"/></svg>
                <span style={{fontSize:12,color:'#4A7C59',fontWeight:500}}>Request submitted! Your manager will review shortly.</span>
              </div>
            )}

            {/* Type selector */}
            <div style={{marginBottom:14}}>
              <label style={{display:'block',fontSize:10,textTransform:'uppercase',letterSpacing:'0.1em',color:'#6B7D6B',fontWeight:600,marginBottom:8}}>Request Type</label>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:6}}>
                {categories.map(t => (
                  <button key={t} onClick={()=>setRequestType(t)}
                    style={{padding:'8px 4px',border:`1px solid ${requestType===t?accent:'#EDE6DA'}`,borderRadius:6,background:requestType===t?accentBg:'#fff',color:requestType===t?accent:'#4A4A4A',fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:"'Montserrat',sans-serif"}}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Dates */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
              <div>
                <label style={{display:'block',fontSize:10,textTransform:'uppercase',letterSpacing:'0.1em',color:'#6B7D6B',fontWeight:600,marginBottom:5}}>Start Date</label>
                <input type="date" value={startDate} onChange={e=>setStartDate(e.target.value)}
                  style={{width:'100%',padding:'9px 13px',border:'1px solid #EDE6DA',borderRadius:6,fontSize:13,fontFamily:"'Montserrat',sans-serif",outline:'none',color:'#2B2B2B'}}/>
              </div>
              <div>
                <label style={{display:'block',fontSize:10,textTransform:'uppercase',letterSpacing:'0.1em',color:'#6B7D6B',fontWeight:600,marginBottom:5}}>End Date</label>
                <input type="date" value={endDate} onChange={e=>setEndDate(e.target.value)}
                  style={{width:'100%',padding:'9px 13px',border:'1px solid #EDE6DA',borderRadius:6,fontSize:13,fontFamily:"'Montserrat',sans-serif",outline:'none',color:'#2B2B2B'}}/>
              </div>
            </div>

            {/* Hours per day */}
            <div style={{marginBottom:14}}>
              <label style={{display:'block',fontSize:10,textTransform:'uppercase',letterSpacing:'0.1em',color:'#6B7D6B',fontWeight:600,marginBottom:8}}>Hours Per Day</label>
              <div style={{display:'flex',gap:6}}>
                {[4, 6, 8].map(h => (
                  <button key={h} onClick={()=>setHoursPerDay(h)}
                    style={{flex:1,padding:'8px',border:`1px solid ${hoursPerDay===h?accent:'#EDE6DA'}`,borderRadius:6,background:hoursPerDay===h?accentBg:'#fff',color:hoursPerDay===h?accent:'#4A4A4A',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:"'Montserrat',sans-serif"}}>
                    {h} hrs
                  </button>
                ))}
                <input type="number" min={1} max={12} value={hoursPerDay} onChange={e=>setHoursPerDay(Number(e.target.value))}
                  style={{width:60,padding:'8px',border:'1px solid #EDE6DA',borderRadius:6,fontSize:12,fontFamily:"'Montserrat',sans-serif",outline:'none',color:'#2B2B2B',textAlign:'center'}}/>
              </div>
              <div style={{fontSize:10,color:'#8A9E8A',marginTop:4}}>{businessDays} business day{businessDays!==1?'s':''} selected</div>
            </div>

            {/* Notes */}
            <div style={{marginBottom:14}}>
              <label style={{display:'block',fontSize:10,textTransform:'uppercase',letterSpacing:'0.1em',color:'#6B7D6B',fontWeight:600,marginBottom:5}}>Notes (optional)</label>
              <textarea rows={2} value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Let your manager know any context..."
                style={{width:'100%',padding:'9px 13px',border:'1px solid #EDE6DA',borderRadius:6,fontSize:13,fontFamily:"'Montserrat',sans-serif",outline:'none',resize:'none',color:'#2B2B2B'}}/>
            </div>

            {/* Hours calc */}
            <div style={{background:'#F7F2EB',border:'1px solid #EDE6DA',borderRadius:8,padding:'12px 16px',marginBottom:14,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span style={{fontSize:12,color:'#6B7D6B'}}>Hours Requested</span>
              <span style={{fontFamily:'Georgia,serif',fontSize:'1.2rem',color:accent,fontWeight:600}}>
                {hoursRequested} <span style={{fontSize:12,color:'#8A9E8A',fontFamily:"'Montserrat',sans-serif",fontWeight:400}}>hrs</span>
              </span>
            </div>

            {/* Balance bar */}
            <div style={{marginBottom:16}}>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'#6B7D6B',marginBottom:4}}>
                <span>Accrued Balance</span>
                <span style={{color:hoursAfter<0?'#A0392B':'#6B7D6B',fontWeight:hoursAfter<0?600:400}}>
                  {hoursAfter < 0 ? `${Math.abs(hoursAfter)} hrs over balance` : `${hoursAfter} hrs remaining after`}
                </span>
              </div>
              <div style={{height:8,background:'#EDE6DA',borderRadius:4,overflow:'hidden',display:'flex'}}>
                <div style={{width:balanceWidth+'%',background:accent,borderRadius:4,transition:'width 0.3s'}}/>
                <div style={{width:requestedWidth+'%',background:'rgba(160,57,43,0.35)',borderRadius:4,transition:'width 0.3s'}}/>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:'#8A9E8A',marginTop:3}}>
                <span>Used: {usedYTD} hrs</span>
                <span>Accrued: {accrued} hrs (1hr per 40hrs worked)</span>
              </div>
            </div>

            {hoursAfter < 0 && (
              <div style={{background:'rgba(160,57,43,0.06)',border:'1px solid rgba(160,57,43,0.2)',borderRadius:6,padding:'8px 12px',marginBottom:14,fontSize:11,color:'#A0392B'}}>
                ⚠ This request exceeds your available balance. Your manager can still approve it at their discretion.
              </div>
            )}

            <button onClick={handleSubmit}
              style={{width:'100%',padding:'10px',background:accent,border:'none',borderRadius:6,color:'#fff',fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:"'Montserrat',sans-serif"}}>
              Submit Request →
            </button>
          </div>
        </div>

        {/* Right column */}
        <div style={{display:'flex',flexDirection:'column',gap:16}}>

          {/* Accrual info */}
          <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>PTO Balance</div>
              <span style={{fontSize:10,padding:'2px 8px',borderRadius:8,fontWeight:600,background:'rgba(107,125,107,0.08)',color:'#6B7D6B'}}>Hours · Maine LD 369</span>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,padding:12}}>
              {[
                { val: `${available}`, unit:'hrs', label:'Available',    color:accent },
                { val: `${accrued}`,   unit:'hrs', label:'Accrued YTD',  color:'#4A7C59' },
                { val: `${usedYTD}`,   unit:'hrs', label:'Used YTD',     color:'#2B2B2B' },
                { val: '1:40',         unit:'',    label:'Accrual Rate', color:'#4A6B8A' },
              ].map(s => (
                <div key={s.label} style={{background:'#F7F2EB',borderRadius:8,padding:'12px',textAlign:'center'}}>
                  <div style={{fontFamily:'Georgia,serif',fontSize:'1.3rem',color:s.color,fontWeight:600,lineHeight:1}}>
                    {s.val}<span style={{fontSize:11,color:'#8A9E8A',fontFamily:"'Montserrat',sans-serif",fontWeight:400}}> {s.unit}</span>
                  </div>
                  <div style={{fontSize:10,color:'#6B7D6B',textTransform:'uppercase',letterSpacing:'0.08em',marginTop:4}}>{s.label}</div>
                </div>
              ))}
            </div>
            <div style={{padding:'10px 14px',borderTop:'1px solid #EDE6DA',background:'#F7F2EB'}}>
              <div style={{fontSize:10,color:'#8A9E8A',lineHeight:1.6}}>
                <strong style={{color:'#6B7D6B'}}>Maine LD 369:</strong> Employees accrue 1 hour of paid leave for every 40 hours worked. Accrual begins at hire; use allowed after 120 days of employment.
              </div>
            </div>
          </div>

          {/* History */}
          <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA'}}>
              <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>Request History</div>
            </div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr style={{background:'#F7F2EB'}}>
                  {['Dates','Type','Hours','Status'].map(h => (
                    <th key={h} style={{textAlign:'left',padding:'8px 16px',fontSize:9,textTransform:'uppercase',letterSpacing:'0.1em',color:'#6B7D6B',fontWeight:600,borderBottom:'1px solid #EDE6DA'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((r,i) => (
                  <tr key={i} style={{borderBottom:'1px solid #EDE6DA'}}>
                    <td style={{padding:'10px 16px',color:'#4A4A4A'}}>{r.dates}</td>
                    <td style={{padding:'10px 16px',color:'#4A4A4A'}}>{r.type}</td>
                    <td style={{padding:'10px 16px',color:'#4A4A4A'}}>{r.hours} hrs</td>
                    <td style={{padding:'10px 16px'}}>
                      <span style={{padding:'2px 8px',borderRadius:8,fontSize:10,fontWeight:600,background:r.statusColor+'22',color:r.statusColor}}>{r.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </div>
      </div>
    </div>
  )
}

